import {
  CreateEventDTO,
  ShortfyEvent,
  UpdateEventDTO,
} from "../types/event.types";
import { ApiResponse } from "../types/shared.types";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://api.shortfy.it";

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const userId = sessionStorage.getItem("userOid");
  if (userId) {
    headers["client-principal-user-id"] = userId;
  }

  return headers;
};

export const getAllEvents = async (options?: {
  upcoming?: boolean;
  includeCompleted?: boolean;
}): Promise<ApiResponse<ShortfyEvent[]>> => {
  try {
    const params = new URLSearchParams();
    if (options?.upcoming) params.append("upcoming", "true");
    if (options?.includeCompleted) params.append("includeCompleted", "true");

    const queryString = params.toString() ? `?${params.toString()}` : "";

    const response = await fetch(
      `${API_BASE_URL}/cgi-events${queryString}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to fetch events" };
  }
};

export const getMyEvents = async (): Promise<ApiResponse<ShortfyEvent[]>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cgi-events/my`, {
      method: "GET",
      headers: getHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to fetch your events" };
  }
};

export const getEventById = async (
  eventId: string,
): Promise<ApiResponse<ShortfyEvent>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cgi-events/${eventId}`, {
      method: "GET",
      headers: getHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to fetch event" };
  }
};

export const createEvent = async (
  data: CreateEventDTO,
): Promise<ApiResponse<ShortfyEvent>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cgi-events`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to create event" };
  }
};

export const updateEvent = async (
  eventId: string,
  data: UpdateEventDTO,
): Promise<ApiResponse<ShortfyEvent>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cgi-events/${eventId}`, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to update event" };
  }
};

export const deleteEvent = async (
  eventId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cgi-events/${eventId}`, {
      method: "DELETE",
      headers: getHeaders(),
    });

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to delete event" };
  }
};

export const cancelEvent = async (
  eventId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/cgi-events/${eventId}/cancel`,
      {
        method: "POST",
        headers: getHeaders(),
      },
    );

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to cancel event" };
  }
};

export const completeEvent = async (
  eventId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/cgi-events/${eventId}/complete`,
      {
        method: "POST",
        headers: getHeaders(),
      },
    );

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to complete event" };
  }
};

export const syncRegistrationCount = async (
  eventId: string,
): Promise<ApiResponse<{ currentRegistrations: number }>> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/cgi-events/${eventId}/sync-registrations`,
      {
        method: "POST",
        headers: getHeaders(),
      },
    );

    return await response.json();
  } catch (error) {
    return { success: false, error: "Failed to sync registration count" };
  }
};
